import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { CurrentTenantService, ProductService, SubscriptionService } from '@suvo-bi-users';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { IFilterProperty } from '../../../../../../shared/interfaces/filters/filter-property.interface';
import { FilterRule } from '../../../../../../shared/interfaces/filters/filter-rule.type';
import { DataSourceItemsService } from '../../../../../data-source/data-source-items/services/data-source-items.service';
import { Filters } from '../../../../../filters/models/filters.model';
import { FiltersChannel } from '../../../../../filters/models/filters-channel.model';
import { DEFAULT_FILTERS_VALUE } from '../../../../constants/default-filters-value.constant';
import { IChartTileDefinition } from '../../../../interfaces/tiles/definitions/chart-tile-definition.interface';
import { LocalChartFilterChange } from '../../../../interfaces/tiles/local-chart-filter-change.interface';
import { ITileComponent } from '../../tile-component.interface';

@Component({
  standalone: false,
  selector: 'suvo-bi-chart-tile',
  templateUrl: './chart-tile.component.html',
  styleUrls: ['./chart-tile.component.scss'],
})
export class ChartTileComponent implements ITileComponent, OnInit, OnChanges, OnDestroy {
  @Input() tileInstance: IChartTileDefinition;
  @Input() filtersChannel: FiltersChannel;
  @Input() editMode: boolean = false;

  filters: Filters;

  realColors = [
    '#0E1035',
    '#104D74',
    '#118AB2',
    '#0CB0A9',
    '#06D6BA',
    '#FFD166',
    '#F78C6B',
    '#EF476F',
    '#C13B8E',
    '#932EAD',
  ];

  // Data
  isLoading = true;
  chartData: any[]; // Not inactivated by legend
  legend: { name: string; inactive?: boolean }[];

  // Settings
  barPadding;
  showXAxis;
  showYAxis;
  gradient;
  showLegend = true;
  legendPosition;
  showXAxisLabel;
  xAxisLabel = '';
  showYAxisLabel;
  yAxisLabel;
  colorScheme = {
    domain: this.realColors,
  };

  private finalFilters: Filters;
  private localChartFilters: { [index: string]: FilterRule } = {};
  private chartRealData: any[];
  private isLoadingNewData = true;
  private readonly $unsubscribe = new Subject<void>();

  isTrialing = false;
  hasRestrictions = false;

  get shouldHideVerticalGridLines(): boolean {
    if (typeof this.tileInstance?.hideVerticalGridLines === 'boolean') {
      return this.tileInstance.hideVerticalGridLines;
    }

    if (typeof this.tileInstance?.hideGridLines === 'boolean') {
      return this.tileInstance.hideGridLines;
    }

    return this.isVerticalBarChart;
  }

  get shouldHideHorizontalGridLines(): boolean {
    if (typeof this.tileInstance?.hideHorizontalGridLines === 'boolean') {
      return this.tileInstance.hideHorizontalGridLines;
    }

    if (typeof this.tileInstance?.hideGridLines === 'boolean') {
      return this.tileInstance.hideGridLines;
    }

    return this.isHorizontalBarChart;
  }

  private get isVerticalBarChart(): boolean {
    return this.tileInstance?.chartType?.value?.includes('bar-vertical') || false;
  }

  private get isHorizontalBarChart(): boolean {
    return this.tileInstance?.chartType?.value?.includes('bar-horizontal') || false;
  }

  constructor(
    private readonly dataService: DataSourceItemsService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly currentTenantService: CurrentTenantService,
    private productService: ProductService,
    private subscriptionService: SubscriptionService,
  ) {}

  ngOnInit() {
    if (!this.tileInstance) {
      return;
    }

    Object.assign(this, this.tileInstance.chartType); //Can we remove this?

    if (this.tileInstance.chartColors) {
      this.realColors = JSON.parse(JSON.stringify(this.tileInstance.chartColors));
    } else {
      const tenantPalette = this.currentTenantService.currentTenant()?.chartColorPalette;
      if (Array.isArray(tenantPalette) && tenantPalette.length > 0) {
        this.realColors = [...tenantPalette];
      }
    }

    if (this.tileInstance.hideLegend) {
      this.showLegend = false;
    }

    if (this.filtersChannel) {
      this.filtersChannel?.filters
        .pipe(
          takeUntil(this.$unsubscribe),
          filter((filters) => !!filters),
        )
        .subscribe((filters) => {
          this.filters = JSON.parse(JSON.stringify(filters));
          this.finalFilters = JSON.parse(JSON.stringify(filters));

          this.setupChart();
        });
    } else {
      if (this.tileInstance.recipe?.filters) {
        this.filters = JSON.parse(JSON.stringify(this.tileInstance.recipe.filters));
        this.finalFilters = JSON.parse(JSON.stringify(this.tileInstance.recipe.filters));
      }

      this.setupChart();
    }

    this.showYAxisLabel = !!this.yAxisLabel;

    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small])
      .pipe(takeUntil(this.$unsubscribe), distinctUntilChanged())
      .subscribe((result) => {
        if (result.matches) {
          this.showXAxis = false;
          this.showXAxisLabel = false;
        } else {
          this.showXAxis = true;
          this.showXAxisLabel = true;
        }
      });

    this.subscriptionService.currentSubscription
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe((currentSubscription) => {
        if (currentSubscription && currentSubscription?.status === 'trialing') {
          this.isTrialing = true;
        }
        if (this.editMode) {
          this.hasRestrictions = false;
        } else {
          this.hasRestrictions = this.productService.hasRestrictions('AGGREGATION', [
            'DATASOURCE:' + this.tileInstance.recipe?.dataSource,
          ]);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['tileInstance'] && !changes['tileInstance'].firstChange) {
      this.setupChart();
    }
  }

  ngOnDestroy() {
    this.$unsubscribe.next();
    this.$unsubscribe.complete();
  }

  labelFormatting(name: string) {
    /** Note: 'this' is the chart component confusingly. Not this tile component. */

    let chartDataItem = (this as any).data?.find((dataItem) => dataItem.data.name == name);

    if (chartDataItem) {
      return `${name} ${Math.floor(chartDataItem.data.value).toLocaleString()}`;
    } else {
      return name;
    }
  }

  updateVisibleChartData(useLegend?: boolean) {
    /*
      Set the correct chartData and colorScheme for the data which has not been legend-disabled.
    */

    let usedColors = [];
    this.chartData = this.chartRealData.filter((data: any, index: number) => {
      if (!useLegend || !this.legend.find((item) => item.name == data.name)?.inactive) {
        usedColors.push(this.realColors[index % this.realColors.length]);
        return true;
      }
      return false;
    });
    this.colorScheme.domain = usedColors;
  }

  onSelect(data) {}

  onActivate(data) {}

  onDeactivate(data) {}

  onChartLocalFilterChange(filterData: LocalChartFilterChange) {
    const newRule: FilterRule = {
      key: filterData.localChartFilter.property,
      con: [
        {
          val: filterData.newValue,
          type: filterData.localChartFilter.operator,
        },
      ],
    };

    if (filterData.newValue) {
      this.localChartFilters[newRule.key] = newRule;
    } else {
      delete this.localChartFilters[newRule.key];
    }

    this.setupChart();
  }

  private setLocalFilters() {
    if (!this.filters) {
      this.filters = DEFAULT_FILTERS_VALUE;
    }
    this.finalFilters = JSON.parse(JSON.stringify(this.filters));

    const { rules } = this.finalFilters.query;

    Object.entries(this.localChartFilters).forEach(([key, value]: [string, IFilterProperty]) => {
      const existedRule: IFilterProperty = rules.find(
        (rule: IFilterProperty) => rule.key === key,
      ) as IFilterProperty;

      if (existedRule) {
        existedRule.con = value.con;
      } else {
        this.finalFilters.query.rules.push(value);
      }
    });
  }

  private setupChart() {
    if (this.tileInstance?.neverLoad) {
      return;
    }

    this.barPadding = 16;
    this.showXAxis = true; // this.breakpointObserver.isMatched('(max-width: 600px)');
    this.showYAxis = true;
    this.gradient = false;
    this.legendPosition = 'below';
    this.showXAxisLabel = true;
    this.xAxisLabel = '';
    this.showYAxisLabel = false;

    this.isLoadingNewData = true;

    if (Array.isArray(this.tileInstance?.staticData)) {
      const chartData = this.tileInstance.staticData
        .map((item) => ({
          name: item?.name,
          value: Number(item?.value) || 0,
        }))
        .filter((item) => !!item.name);

      this.chartRealData = chartData;
      this.updateVisibleChartData();
      this.legend = chartData.map((data: any) => ({ name: data.name }));
      this.isLoading = false;
      this.isLoadingNewData = false;
      return;
    }

    //const newChartData = this.fakeData;

    this.setLocalFilters();

    const compositeRecipe = { ...this.tileInstance.recipe };
    if (this.finalFilters) {
      compositeRecipe.filters = this.finalFilters;
    }

    this.dataService.getAggregated(compositeRecipe).then((response) => {
      response = (response as Array<any>).map((item) => {
        if (item.group && compositeRecipe.groupLabelField) {
          return {
            name: item.group[compositeRecipe.groupLabelField],
            value: item.value,
          };
        } else {
          return { name: item._id, value: item.value };
        }
      });

      let chartData = response as any[];

      chartData = chartData.filter((data) => data.value > 0 && data.name);

      const legend = chartData.map((data: any, index: number) => {
        // color uses modulus incase there aren't enough colors.
        return { name: data.name };
      });

      // this.legend = chartData.map((d: any) => d.name)
      this.chartRealData = chartData;
      this.updateVisibleChartData();
      this.legend = legend;
      this.isLoading = false;
      this.isLoadingNewData = false;
    });
  }
}
